import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";
import { useAuthStore } from "@/store/auth";
import { ApiError, swrFetcher } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs } from "@/components/ui/tabs";
import { PandaFilmsLogo } from "@/components/BrandLogo";

export function LoginPage() {
  const navigate = useNavigate();
  const { login, register } = useAuthStore();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: bootstrap } = useSWR<{ registration_open: boolean }>(
    "/api/auth/bootstrap-status",
    swrFetcher
  );

  useEffect(() => {
    if (bootstrap && !bootstrap.registration_open) setMode("login");
  }, [bootstrap]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (mode === "login") {
        const session = await login(email, password);
        if (session.requires_password_change) {
          navigate("/change-password");
          return;
        }
      } else {
        await register(email, password, fullName || undefined);
      }
      navigate("/");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mobile-safe-top mobile-safe-bottom flex min-h-dvh items-center justify-center px-4 py-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <PandaFilmsLogo className="mx-auto mb-4 h-36 w-36" />
          <h1 className="sr-only">PandaFilms</h1>
          <p className="text-xs text-studio-muted">Self-hosted multi-provider media platform</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-xl border border-studio-border bg-studio-panel p-4 shadow-panel sm:rounded-2xl sm:p-6"
        >
          {bootstrap?.registration_open ? (
            <Tabs
              fullWidth
              tabs={[
                { value: "login", label: "Login" },
                { value: "register", label: "First-time setup" },
              ]}
              value={mode}
              onChange={(m) => setMode(m)}
            />
          ) : (
            <div>
              <h2 className="text-wrap-balance text-lg font-semibold text-studio-ink">Welcome back</h2>
              <p className="mt-1 text-xs leading-5 text-studio-muted">Sign in with your account or temporary password.</p>
            </div>
          )}

          {mode === "register" && (
            <label className="block text-xs font-medium text-studio-muted">
              Full name
              <Input className="mt-1.5" type="text" placeholder="Full name (optional)" value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </label>
          )}

          <label className="block text-xs font-medium text-studio-muted">
            Email
            <Input className="mt-1.5" type="email" autoComplete="email" placeholder="you@example.com" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>

          <label className="block text-xs font-medium text-studio-muted">
            Password
            <Input className="mt-1.5" type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} placeholder="At least 8 characters" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
          </label>

          {error && (
            <p className="rounded-lg bg-studio-danger/10 px-3 py-2 text-xs text-studio-danger">{error}</p>
          )}

          <Button type="submit" loading={loading} size="lg" className="w-full">
            {mode === "login" ? "Login" : "Create account"}
          </Button>
        </form>
      </div>
    </div>
  );
}
